if (!customElements.get('pt-purchase-options')) {
  customElements.define('pt-purchase-options', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.data = JSON.parse(this.querySelector('[data-purchase-data]').textContent);
      this.form = this.querySelector('form');
      this.querySelector('.pt-purchase-variants').disabled = false;
      this.variantId = this.form.elements.id.value;
      this.mode = this.constructor.defaultPlan(this.data.variants.find(variant => variant.id === this.variantId)) ? 'subscription' : 'one-time';
      const hasSubscriptions = this.data.variants.some(variant => variant.plans.length);
      const hasOneTime = this.data.variants.some(variant => !variant.requiresPlan);
      this.querySelector('[data-type-options]').hidden = !(hasSubscriptions && hasOneTime);
      this.form.addEventListener('change', event => {
        if (!['purchase_type', 'purchase_variant'].includes(event.target.name)) return;
        if (event.target.name === 'purchase_type') {
          this.mode = event.target.value;
        }
        if (event.target.name === 'purchase_variant') {
          this.variantId = event.target.value;
        }
        this.update();
      });
      this.form.addEventListener('submit', event => {
        this.update();
        if (this.form.querySelector('[name="add"]').disabled) event.preventDefault();
      });
      this.update();
      this.checkoutObserver = new MutationObserver(() => this.positionCheckoutLinks());
      this.checkoutObserver.observe(this.querySelector('[data-accelerated-checkout]'), {
        childList: true, subtree: true, attributes: true, attributeFilter: ['disabled']
      });
      this.positionCheckoutLinks();
    }

    positionCheckoutLinks() {
      const footer = this.querySelector('[data-checkout-links]');
      if (!footer) return;
      const checkout = this.querySelector('shopify-accelerated-checkout');
      const link = checkout?.querySelector('more-payment-options-link');
      if (link?.querySelector('a')) {
        footer.querySelector('more-payment-options-link')?.remove();
        link.removeAttribute('slot');
        footer.prepend(link);
      }
      const movedLink = footer.querySelector('more-payment-options-link');
      if (movedLink && checkout) movedLink.disabled = checkout.disabled || this.form.querySelector('[name="add"]').disabled;
      const terms = this.querySelector('.pt-subscription-terms');
      if (terms && terms.parentElement !== footer) footer.append(terms);
    }

    static defaultPlan(variant) {
      if (!variant) return null;
      const bottles = variant.title.match(/\b(1|3|6)\s*bottles?\b/i)?.[1];
      const days = { '1': 30, '3': 90, '6': 180 }[bottles];
      return variant.plans.find(plan => days && new RegExp('\\b' + days + '[ -]*days?\\b', 'i').test(plan.name)) || variant.plans[0] || null;
    }

    static selection(variants, mode, variantId) {
      const eligible = variants.filter(variant => mode === 'subscription' ? variant.plans.length : !variant.requiresPlan);
      const variant = eligible.find(item => item.id === variantId && item.available) || eligible.find(item => item.available) || eligible[0];
      const plan = mode === 'subscription' && variant ? this.defaultPlan(variant) : null;
      return { variant, plan };
    }

    updateSubscriptionTerms(hasPlan) {
      const consent = this.querySelector('[data-consent-type="subscription"]');
      if (!consent) return;
      let details = consent.closest('.pt-subscription-terms');
      if (!details) {
        details = document.createElement('details');
        details.className = 'pt-subscription-terms';
        const summary = document.createElement('summary');
        summary.textContent = 'Subscription terms \u24d8';
        details.append(summary);
        consent.before(details);
        // Preserve Shopify's original notice, policy control, and visibility handling.
        details.append(consent);
      }
      details.hidden = !hasPlan;
      if (!hasPlan) details.open = false;
      this.positionCheckoutLinks();
    }

    update() {
      const { variant, plan } = this.constructor.selection(this.data.variants, this.mode, this.variantId);
      this.variantId = variant?.id || '';
      this.form.elements.id.value = this.variantId;
      this.form.elements.quantity.value = variant?.quantity || 1;
      this.form.elements.selling_plan.value = plan?.id || '';
      this.form.elements.selling_plan.disabled = !plan;
      this.querySelectorAll('[name="purchase_type"]').forEach(input => { input.checked = input.value === this.mode; });
      this.querySelectorAll('[data-variant]').forEach(row => {
        const item = this.data.variants.find(item => item.id === row.dataset.variant);
        const allocation = this.mode === 'subscription' ? (item.id === this.variantId ? plan : this.constructor.defaultPlan(item)) : null;
        row.hidden = this.mode === 'subscription' ? !allocation : item.requiresPlan;
        const input = row.querySelector('input');
        input.checked = item.id === this.variantId;
        input.disabled = row.hidden || !item.available;
        row.classList.toggle('is-selected', input.checked);
        row.querySelector('[data-price]').textContent = allocation?.money || item.money;
        row.querySelector('[data-description]').textContent = !item.available ? this.data.soldOutLabel : allocation ? allocation.name : 'One-time purchase';
        const saving = allocation && item.price > 0 ? Math.round((item.price - allocation.price) * 100 / item.price) : 0;
        row.querySelector('[data-saving]').hidden = saving <= 0;
        row.querySelector('[data-saving]').textContent = saving > 0 ? 'Save ' + saving + '%' : '';
      });
      const description = plan ? plan.name + '. Pause or cancel anytime.' : 'One-time purchase. No recurring charges.';
      this.querySelector('[data-summary]').textContent = description;
      const button = this.form.querySelector('[name="add"]');
      button.disabled = !variant?.available || (this.mode === 'subscription' && !plan);
      this.querySelector('[data-accelerated-checkout]').hidden = button.disabled;
      this.updateSubscriptionTerms(Boolean(plan));
      // Notify Shopify's payment buttons after all purchase inputs are synchronized.
      this.form.elements.id.dispatchEvent(new Event('change', { bubbles: true }));
      this.form.elements.selling_plan.dispatchEvent(new Event('change', { bubbles: true }));
      this.querySelector('[data-button-text]').textContent = button.disabled ? this.data.soldOutLabel : this.data.addLabel + ' - ' + (plan?.money || variant.money);
      // Keep the optional sticky add-to-cart bar consistent with this selection.
      const section = this.closest('.shopify-section');
      if (section?.querySelector('pt-purchase-options') === this) {
        document.querySelectorAll('.fixed-title').forEach(el => { el.textContent = variant?.title || ''; });
        document.querySelectorAll('.fixed-small-desc').forEach(el => { el.textContent = description; });
        document.querySelectorAll('.fixed-price').forEach(el => { el.textContent = plan?.money || variant?.money || ''; });
      }
      const cancelInfo = this.closest('[data-purchase-wrapper]')?.querySelector('[data-subscription-assurance]');
      if (cancelInfo) cancelInfo.hidden = !plan;
    }
  });
}
